/**
 * Translation utilities
 *
 * Provides type-safe translation functions and locale types.
 */

// Re-export types for consumers
export type {
  TranslationKey,
  TypedLocale,
  InterpolatedTranslationKey,
  SimpleTranslationKey,
  SupportedLanguage,
  TranslateFunction,
} from "./types";

/**
 * Locale value type - either a string or a function returning a string
 */
export type LocaleValue = string | ((arg: string) => string);

/**
 * Locale object type - maps keys to their values
 */
export type Locale = Record<string, LocaleValue>;

/**
 * Translate function
 *
 * Translates a key from the given locale, optionally with interpolation args.
 *
 * @param locale - The locale object containing translations
 * @param key - The translation key
 * @param args - Optional arguments for interpolated translations
 * @returns The translated string
 */
export const translate = (
  locale: Locale,
  key: keyof Locale,
  ...args: string[]
): string => {
  const value = locale[key];

  if (typeof value === "function") {
    return value(args[0] ?? "");
  }

  return (value as string) || String(key);
};
